import { OperationResult, useMutation } from "urql";

import {
  MakeMeteredBillingPortalLinkInput,
  UpdateWorkspaceSubscriptionInput,
} from "@/components/graphql/schema.graphql";
import {
  MakeCreateSubscriptionCheckoutLink,
  MakeCreateSubscriptionCheckoutLinkMutationResult,
  MakeMeteredBillingPortalLink,
  MakeOneTimePurchaseCheckoutLink,
  MakeOneTimePurchaseCheckoutLinkMutationResult,
  MakeSubscriptionTopOffCheckoutLink,
  MakeSubscriptionTopOffCheckoutLinkMutationResult,
  UpdateWorkspaceSubscription,
} from "@/graphql/billing.graphql";

import { useUrqlResponseHandler } from "./useUrqlResponseHandler";

interface SubscriptionCheckoutInput {
  workspaceId: string;
  units: number;
  annualBilling?: boolean;
  successPath?: string;
  cancelPath?: string;
}

interface OneTimeCheckoutInput {
  workspaceId: string;
  units: number;
  successPath?: string;
  cancelPath?: string;
}

type CheckoutLinkResponse =
  | OperationResult<MakeCreateSubscriptionCheckoutLinkMutationResult>
  | OperationResult<MakeSubscriptionTopOffCheckoutLinkMutationResult>
  | OperationResult<MakeOneTimePurchaseCheckoutLinkMutationResult>;

export const useBilling = () => {
  const { getResponseFromState } = useUrqlResponseHandler();
  const [, makeCreateSubscriptionCheckoutLink] = useMutation(
    MakeCreateSubscriptionCheckoutLink,
  );

  const [, makeSubscriptionTopOffCheckoutLink] = useMutation(
    MakeSubscriptionTopOffCheckoutLink,
  );
  const [, makeOneTimePurchaseCheckoutLink] = useMutation(
    MakeOneTimePurchaseCheckoutLink,
  );
  const [, makeMeteredBillingPortalLink] = useMutation(
    MakeMeteredBillingPortalLink,
  );
  const [, updateWorkspaceSubscription] = useMutation(
    UpdateWorkspaceSubscription,
  );

  const extractCheckoutUrl = (data: CheckoutLinkResponse["data"]) => {
    if (!data) return null;

    if ("makeCreateSubscriptionCheckoutLink" in data) {
      const result = data.makeCreateSubscriptionCheckoutLink;
      return result?.__typename === "Link" ? result.url : null;
    }
    if ("makeSubscriptionTopOffCheckoutLink" in data) {
      const result = data.makeSubscriptionTopOffCheckoutLink;
      return result?.__typename === "Link" ? result.url : null;
    }
    if ("makeOneTimePurchaseCheckoutLink" in data) {
      const result = data.makeOneTimePurchaseCheckoutLink;
      return result?.__typename === "Link" ? result.url : null;
    }

    return null;
  };

  const openCheckoutWindow = (checkoutLinkResponse: CheckoutLinkResponse) => {
    if (!checkoutLinkResponse) {
      console.error("Failed to generate checkout link");
      return;
    }

    const { data } = checkoutLinkResponse;
    const checkoutLink = extractCheckoutUrl(data);

    if (checkoutLink) {
      const newWindow = window.open(
        checkoutLink,
        "_blank",
        "noopener,noreferrer",
      );
      if (newWindow) {
        newWindow.opener = null;
      }
    }
  };

  const goToSubscriptionCheckout = async (input: SubscriptionCheckoutInput) => {
    const { workspaceId, units, annualBilling, successPath, cancelPath } =
      input;

    const checkoutLinkResponse = await makeCreateSubscriptionCheckoutLink({
      input: {
        workspaceId,
        units,
        annualBilling: annualBilling ?? false,
        successPath,
        cancelPath,
      },
    });

    openCheckoutWindow(checkoutLinkResponse);
  };

  const goToSubscriptionTopUpCheckout = async (input: OneTimeCheckoutInput) => {
    const { workspaceId, units, successPath, cancelPath } = input;

    const checkoutLinkResponse = await makeSubscriptionTopOffCheckoutLink({
      input: {
        workspaceId,
        units,
        successPath,
        cancelPath,
      },
    });

    openCheckoutWindow(checkoutLinkResponse);
  };

  const goToOneTimePurchaseCheckout = async (input: OneTimeCheckoutInput) => {
    const { workspaceId, units, successPath, cancelPath } = input;

    const checkoutLinkResponse = await makeOneTimePurchaseCheckoutLink({
      input: {
        workspaceId,
        units,
        successPath,
        cancelPath,
      },
    });

    openCheckoutWindow(checkoutLinkResponse);
  };

  const getBillingPortalLink = async (
    input: MakeMeteredBillingPortalLinkInput,
  ) => {
    const responseState = getResponseFromState({
      state: await makeMeteredBillingPortalLink({ input }),
    });
    const linkResult = responseState?.data?.makeMeteredBillingPortalLink;
    return linkResult?.url;
  };

  const goToBillingPortalLink = async (
    input: MakeMeteredBillingPortalLinkInput,
  ) => {
    const billingPortalLink = await getBillingPortalLink(input);
    if (billingPortalLink) {
      const newWindow = window.open(
        billingPortalLink,
        "_blank",
        "noopener,noreferrer",
      );
      if (newWindow) {
        newWindow.opener = null;
      }
    }
  };

  const updateSubscription = async (
    input: UpdateWorkspaceSubscriptionInput,
  ) => {
    const responseState = getResponseFromState({
      state: await updateWorkspaceSubscription({ input }),
    });
    return responseState?.data?.updateWorkspaceSubscription;
  };

  return {
    goToSubscriptionCheckout,
    goToSubscriptionTopUpCheckout,
    goToOneTimePurchaseCheckout,
    goToBillingPortalLink,
    updateSubscription,
  };
};
